import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { DashboardLayout } from '../../../components/dashboard/DashboardLayout';
import { QRCodeHeader } from './components/QRCodeHeader';
import { StatsGrid } from './components/StatsGrid';
import { AnalyticsCharts } from './components/AnalyticsCharts';
import { RoutingRulesSection } from './components/RoutingRulesSection';
import { sampleQRCodeData, sampleAnalytics, SAMPLE_QR_SHORT_CODE } from '../../../data/sampleQrCode';
import { useAuth } from '../../../contexts/AuthContext';

const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3, ease: 'easeOut' }
  }
};

// Renders the same layout as QRCodeDetailPage, but fed entirely from hardcoded
// data (frontend/src/data/sampleQrCode.ts) instead of the API, so a new user
// can see what a real QR code with traffic looks like before creating one.
export function SampleQRCodeDetailPage() {
  const navigate = useNavigate();
  const { hideSampleDashboard } = useAuth();

  const handleEditClick = () => {
    navigate(`/dashboard/qr-codes/${SAMPLE_QR_SHORT_CODE}/edit`);
  };

  const handleHide = async () => {
    await hideSampleDashboard();
    navigate('/dashboard/qr-codes');
  };

  return (
    <DashboardLayout currentPage="qr-codes">
      <motion.div
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto space-y-6"
      >
        <div className="flex items-center justify-between bg-[--clinqr-primary]/5 border border-dashed border-[--clinqr-primary]/30 rounded-xl px-4 py-3">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[--clinqr-primary] bg-[--clinqr-primary]/10 px-2 py-1 rounded-full">
              Demo QR code
            </span>
            <p className="text-sm text-[--clinqr-text-secondary] mt-2">
              This is a preview with sample data so you can see what a QR code with real
              traffic looks like. You can open Edit and try out the routing rules editor,
              but changes here are never saved.
            </p>
          </div>
          <button
            onClick={handleHide}
            className="flex items-center gap-1 text-xs text-[--clinqr-text-muted] hover:text-[--clinqr-text-primary] transition-colors flex-shrink-0 ml-4"
            aria-label="Hide demo QR code"
          >
            <X className="w-3.5 h-3.5" />
            Hide
          </button>
        </div>

        <QRCodeHeader
          qrCode={sampleQRCodeData}
          analytics={sampleAnalytics}
          onEditClick={handleEditClick}
        />

        <StatsGrid analytics={sampleAnalytics} isLoading={false} />

        <AnalyticsCharts analytics={sampleAnalytics} isLoading={false} />

        <RoutingRulesSection qrCode={sampleQRCodeData} />
      </motion.div>
    </DashboardLayout>
  );
}

export default SampleQRCodeDetailPage;
